import { apiFiles } from "@/api/file.api.ts";
import {
  ConfirmationModal,
  useModals,
  Box,
  Button,
  ButtonLink,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Text,
  Title,
  PdfViewer,
  Skeleton,
} from "@skailer-com/uikit";
import { linkifyText } from "@/helpers/utils.helper.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import { usePrivateFilePreview } from "@/hooks/use-private-file-preview.hook.ts";
import { PrivateFilePreviewModal } from "@/components/documents/widgets/PrivateFilePreviewModal.tsx";
import { UseDocumentPreviewStateParams } from "@/pages/employees/employee/widgets/use-document-preview-state.hook.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApplicantCVTextEditModal } from "@/pages/recruit/candidates/widgets/ApplicantCVTextEditModal.tsx";
import { ApplicantUploadCVModal } from "@/pages/recruit/candidates/widgets/ApplicantUploadCVModal.tsx";
import {
  useApplicantCVPreviewRefetcher,
  useApplicantCVDelete,
} from "@/queries/recruit/use-applicants.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiApplicantCV,
  ApiApplicantCVEducation,
  ApiApplicantCVExperience,
  ApiApplicantCVLanguage,
  ApiApplicantCVSkill,
} from "@/types/recruit/applicant.cv.types.ts";
import { ApiApplicant } from "@/types/recruit/applicant.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";

export interface ApplicantProfileCVProps {
  applicant: ApiApplicant;
  cv: ApiApplicantCV;
}

export default function ApplicantProfileCV({ applicant, cv }: ApplicantProfileCVProps) {
  const { t } = useTranslation();
  const { formatExperiencePeriod, renderRelativeDate, formatDate } = useFormatter();
  const { renderEmployeeName } = useFormatter();
  const modalsService = useModals();
  const { isExporting, downloadFileFromUrl } = useDownloadHelper();
  const { mutateAsync: deleteCV } = useApplicantCVDelete();
  const { canIEdit } = usePermissions();
  const canManageCandidates = canIEdit(ApiPermission.manage_candidates);
  const canSeeOriginalResume = canIEdit(ApiPermission.see_original_resume);

  const refetchPreview = useApplicantCVPreviewRefetcher(applicant.id, cv.id);

  const handleDownloadCV = useCallback(
    async (type: "original" | "pdf" | "docx") => {
      if (!cv.source_file || (type === "original" && !canSeeOriginalResume)) return;

      if (type == "original") {
        const { data: signedLink } = await apiFiles.getLink(cv.source_file.uuid, 1);
        if (!signedLink) return;
        downloadFileFromUrl(signedLink.download_url);

        analyticsService.trackEvent(analyticEvents.applicants.cvDownloaded, {
          [analyticProperties.id]: applicant.id,
          [analyticProperties.format]: type,
        });
      } else {
        modalsService.openModal(ApplicantUploadCVModal, {
          applicant: applicant,
          cv: cv,
          type: type,
        });
      }
    },
    [applicant, cv, canSeeOriginalResume, downloadFileFromUrl, modalsService]
  );

  const calculateEducationPeriod = (education: ApiApplicantCVEducation) => {
    if (!education.education_date_from) return null;

    if (
      !education.education_date_from.year &&
      !education.education_date_from.month &&
      !education.education_date_from.day
    ) {
      if (!education.education_date_to) return null;
      else return formatDate(education.education_date_to);
    }

    const from = education.education_date_from;
    const to = education.education_date_to;
    return formatExperiencePeriod(from, to);
  };

  const handleOpenCVPreview = useCallback(() => {
    if (!cv.source_file || !canSeeOriginalResume) return;
    modalsService.openModal(
      PrivateFilePreviewModal,
      {
        file: cv.source_file,
        preview: cv.preview,
        refetchPreview,
        analytics: {
          viewed: analyticEvents.applicants.cvViewed,
          downloaded: analyticEvents.applicants.cvDownloaded,
          properties: {
            [analyticProperties.id]: applicant.id,
            [analyticProperties.source]: "Applicant CV Preview Modal",
            [analyticProperties.format]: "original",
          },
        },
      },
      { title: `${cv.personal_info.first_name} ${cv.personal_info.last_name}` }
    );
  }, [applicant.id, cv, canSeeOriginalResume, refetchPreview, modalsService]);

  const handleEditCVText = useCallback(() => {
    if (!cv.cv_text) return;

    modalsService.openModal(ApplicantCVTextEditModal, {
      applicantId: applicant.id,
      applicantCVText: cv.cv_text,
      applicantCVId: cv.id,
      onSave: (updatedCVText: string) => {
        cv.cv_text = updatedCVText;
      },
    });
  }, [applicant.id, cv, modalsService]);

  const handleDeleteCV = useCallback(() => {
    modalsService.openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          await deleteCV({ applicantId: applicant.id, cvId: cv.id });
        } catch {}
      },
    });
  }, [applicant.id, cv.id, deleteCV, modalsService, t]);

  function renderEducationFragment(education: ApiApplicantCVEducation, index: number) {
    return (
      <Fragment key={`education-${index}`}>
        <Stack>
          <Text className={"text-title text-lg"}>{education.title}</Text>
          <Text className={"text-default"}>
            {[education.level, education.field_of_study].filter(Boolean).join(", ")}
          </Text>
          {calculateEducationPeriod(education) && (
            <Text className={"text-secondary"}>{calculateEducationPeriod(education)}</Text>
          )}

          <Text className={"text-default"}>
            {education.location.city}
            {education.location.address && `. ${education.location.address}`}
            {education.location.country && ` (${education.location.country})`}
          </Text>
        </Stack>
        <hr />
      </Fragment>
    );
  }

  const updatedInformation =
    `${t("ats:cv_updated_at")} ${renderRelativeDate(cv.datetime_updated_at)}` +
    (cv.author ? `, ${t("common:created_by").toLowerCase()} ${renderEmployeeName(cv.author)}` : "");
  const canViewOriginalLink = canSeeOriginalResume && Boolean(cv.source_url);
  const canEditCvText = canManageCandidates && !cv.source_url && Boolean(cv.cv_text);
  const canDownloadConverted = canManageCandidates;
  const canDownloadOriginal = canSeeOriginalResume && !cv.source_url;
  const canOpenDownloadMenu = canDownloadConverted || canDownloadOriginal;
  const canShowActionButtons =
    canViewOriginalLink || canEditCvText || canOpenDownloadMenu || canManageCandidates;

  return (
    <>
      <Stack direction={"vertical"} className={"mt-(--gap-xl)"} gap={"xl"}>
        <Stack gap={"sm"}>
          <Title
            header={`${cv.personal_info.first_name} ${cv.personal_info.last_name}`}
            caption={updatedInformation}
          />
          {canShowActionButtons && (
            <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
              {canViewOriginalLink && (
                <ButtonLink
                  variant={"secondary"}
                  href={cv.source_url!}
                  size={"sm"}
                  blank
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-arrow-up-right-from-square"} />}
                >
                  {t("ats:view_original")}
                </ButtonLink>
              )}
              {canEditCvText && (
                <Button
                  variant={"secondary"}
                  onClick={handleEditCVText}
                  size={"sm"}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-file-lines" />}
                >
                  {t("ats:edit_cv_text")}
                </Button>
              )}
              {canOpenDownloadMenu && (
                <DropdownMenu
                  trigger={
                    <Button
                      variant={"secondary"}
                      isLoading={isExporting}
                      size={"sm"}
                      leftIcon={<FontAwesomeIcon icon={"fa-light fa-download"} />}
                    >
                      {`${t("common:download")}`}
                    </Button>
                  }
                >
                  {canDownloadConverted && (
                    <DropdownMenuItem
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-file-pdf" />}
                      onClick={() => handleDownloadCV("pdf")}
                      title={"PDF ..."}
                    />
                  )}
                  {canDownloadConverted && (
                    <DropdownMenuItem
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-file-word" />}
                      onClick={() => handleDownloadCV("docx")}
                      title={t("common:word_file") + " (.docx) ..."}
                    />
                  )}
                  {canDownloadOriginal && (
                    <DropdownMenuItem
                      onClick={() => handleDownloadCV("original")}
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-file" />}
                      title={t("common:original_file")}
                      description={t("ats:as_it_was_uploaded")}
                    />
                  )}
                </DropdownMenu>
              )}
              {canManageCandidates && (
                <Button
                  variant={"danger"}
                  onClick={handleDeleteCV}
                  size={"sm"}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                >
                  {t("common:delete")}...
                </Button>
              )}
            </Stack>
          )}
        </Stack>
        {canSeeOriginalResume && cv.source_file && (
          <ApplicantCVFilePreview
            file={cv.source_file}
            preview={cv.preview}
            refetchPreview={refetchPreview}
            onOpenModal={handleOpenCVPreview}
            onDownload={() => {
              void handleDownloadCV("original");
            }}
          />
        )}
        <hr />
        {cv.cv_text && (
          <>
            <div
              className="text-secondary whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: linkifyText(cv.cv_text) }}
            />
            <hr />
          </>
        )}
        {cv.experience && cv.experience.length > 0 && (
          <>
            <Stack>
              <Title header={t("ats:experience")} size={"xs"} />

              <Stack gap={"lg"} className={"pt-(--gap-md)"}>
                {cv.experience.map((experience: ApiApplicantCVExperience, index: number) => (
                  <Fragment key={`experience-${index}`}>
                    <Stack>
                      <Text className={"text-title text-lg"}>{experience.company}</Text>
                      <Text className={"text-default"}>{experience.position}</Text>

                      <Text className={"text-secondary"}>
                        {experience.position_date_from &&
                          formatExperiencePeriod(
                            experience.position_date_from,
                            experience.position_date_to
                          )}
                      </Text>

                      {experience.description && (
                        <Box className={"pt-(--gap-md)"}>
                          <div
                            className="default whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{
                              __html: linkifyText(experience.description),
                            }}
                          />
                        </Box>
                      )}
                    </Stack>

                    <hr />
                  </Fragment>
                ))}
              </Stack>
            </Stack>
          </>
        )}

        {cv.education && cv.education.length > 0 && (
          <>
            <Stack>
              <Title header={t("ats:education")} size={"xs"} />
              <Stack gap={"lg"} className={"pt-(--gap-md)"}>
                {cv.education.map((education: ApiApplicantCVEducation, index: number) =>
                  renderEducationFragment(education, index)
                )}
              </Stack>
            </Stack>
          </>
        )}

        {cv.additional_education && cv.additional_education.length > 0 && (
          <>
            <Stack>
              <Title header={t("ats:additional_education")} size={"xs"} />
              <Stack gap={"lg"} className={"pt-(--gap-md)"}>
                {cv.additional_education.map(
                  (additional_education: ApiApplicantCVEducation, index: number) =>
                    renderEducationFragment(additional_education, index)
                )}
              </Stack>
            </Stack>
          </>
        )}

        {cv.certificates && cv.certificates.length > 0 && (
          <>
            <Stack>
              <Title header={t("ats:certificates")} size={"xs"} />
              <Stack gap={"lg"} className={"pt-(--gap-md)"}>
                {cv.certificates.map((certificate: ApiApplicantCVEducation, index: number) =>
                  renderEducationFragment(certificate, index)
                )}
              </Stack>
            </Stack>
          </>
        )}

        {cv.skills && cv.skills.length > 0 && (
          <>
            <Stack>
              <Title header={t("ats:skills")} size={"xs"} />
              <Stack gap={"xs"} direction={"horizontal"} className={"flex-wrap pt-(--gap-md)"}>
                {cv.skills.map((skill: ApiApplicantCVSkill, index: number) => (
                  <Text
                    className={"py-1 px-2 bg-dark/5 text-secondary rounded-xs"}
                    key={`skill${index}`}
                  >
                    {skill.title}
                  </Text>
                ))}
              </Stack>
            </Stack>
            <hr />
          </>
        )}
        {cv.languages && cv.languages.length > 0 && (
          <>
            <Stack>
              <Title header={t("common:languages")} size={"xs"} />
              <Stack gap={"sm"} className={"mt-(--gap-md)"}>
                {cv.languages.map((language: ApiApplicantCVLanguage) => (
                  <Stack key={language.title}>
                    <Text className={"text-title text-lg"}>{language.title}</Text>
                    {language.level && <Text className={"text-default"}>{language.level}</Text>}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </>
  );
}

function ApplicantCVFilePreview({
  onOpenModal,
  onDownload,
  ...params
}: UseDocumentPreviewStateParams & { onOpenModal: () => void; onDownload: () => void }) {
  const { t } = useTranslation();
  const { previewUrl, sourceFile, status, isLoading } = usePrivateFilePreview(params);
  const isPdf = sourceFile?.mime_type.toLowerCase() === "application/pdf";
  if (status === "unsupported") return null;
  return (
    <Stack gap={"sm"}>
      <Stack direction={"horizontal"} className={"justify-end"}>
        <Button
          variant={"secondary"}
          size={"sm"}
          onClick={onOpenModal}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-expand"} />}
        >
          {t("ats:view_in_modal")}
        </Button>
      </Stack>
      {!(status === "ready" && !isPdf) && (
        <Box className="h-[600px] min-w-0 overflow-hidden border-1 border-dark/10">
          {status === "pending" ? (
            <Stack items="center" justify="center" className="h-full">
              <FontAwesomeIcon icon="fa-light fa-spinner" className="animate-spin" />
              <Text>{t("common:document_preview_preparing")}</Text>
              <Text>{t("common:document_preview_preparing_description")}</Text>
            </Stack>
          ) : status === "failed" ? (
            <Stack items="center" justify="center" className="h-full">
              <Text>{t("common:document_preview_failed")}</Text>
              <Button variant="secondary" onClick={onDownload}>
                {t("common:download")}
              </Button>
            </Stack>
          ) : isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <PdfViewer src={previewUrl} className="h-full" />
          )}
        </Box>
      )}
    </Stack>
  );
}
